import { useCallback } from "react";

import useEditableCell from "@/react/components/table/useEditableCell";
import { KEY } from "@/react/components/table/useTableMutations";
import DynamicSelectCell from "@/react/features/smp/cells/DynamicSelectCell";

import { AgentOption } from "./CustomOptions";
import { SingleValue } from "./SingleValue";
import { isNil } from "lodash";

const components = { SingleValue, Option: AgentOption };

const computeAgents = (queryFunctions, initialAgentName, queueId) => {
  const currentAgent = queryFunctions?.getAgentByUsername(initialAgentName);
  const availableAgents =
    queryFunctions?.getAgentsAvailableForQueue(queueId) ?? [];
  let agents = [];
  if (!isNil(currentAgent)) {
    agents.push(currentAgent);
  }
  if (availableAgents.length > 0) {
    agents = agents.concat(availableAgents);
  }
  return agents;
};

const AgentCell = (cellProps) => {
  const { updateRows, skipAutoResetPageIndex, schema, queryFunctions } =
    cellProps.table.options.meta;

  const {
    initialValue: initialAgentName,
    value: agentName,
    onChange: onChangeAgentName,
  } = useEditableCell(cellProps);

  const agentIdCell = cellProps.row
    .getAllCells()
    .find((c) => c.column.id === schema.fields.agentId.spec.key)
    .getContext();
  const { value: agentId, onChange: onChangeAgentId } =
    useEditableCell(agentIdCell);

  const onBlur = useCallback(() => {
    skipAutoResetPageIndex();
    updateRows(
      [cellProps.row.original[KEY]],
      new Map([
        [agentIdCell.column.id, agentId],
        [cellProps.column.id, agentName],
      ]),
      schema
    );
  }, [
    agentIdCell,
    cellProps,
    agentId,
    agentName,
    updateRows,
    skipAutoResetPageIndex,
    schema,
  ]);

  const queueIdCell = cellProps.row
    .getAllCells()
    .find((c) => c.column.id === schema.fields.queueId.spec.key)
    .getContext();
  const queueId = queueIdCell.getValue();

  const agents = computeAgents(queryFunctions, initialAgentName, queueId);

  const onChange = (opt) => {
    onChangeAgentId(opt?.agent.sso.sso_id);
    onChangeAgentName(opt?.label);
  };

  // It's important to have the `value` be the same as the `label`,
  // so that the DynamicSelectCell can find and display the currently selected option in the options list;
  // it looks for the selected option based on the `value` property
  const options = agents.map((agent) => ({
    searchQuery: `${agent.profile?.first_name} ${agent.profile?.last_name} ${agent.profile?.email} ${agent.id}`,
    value: agent.sso.sso_username,
    label: agent.sso.sso_username,
    agent: agent,
  }));

  return (
    <DynamicSelectCell
      {...cellProps}
      options={options}
      isFetching={false}
      placeholder={"Select Agent"}
      testId={"agent-select"}
      components={components}
      onBlur={onBlur}
      onChange={onChange}
    />
  );
};

export default AgentCell;
